import * as S from "@radix-ui/react-select"
import { cn, tv, VariantProps } from "@utils/tw"
import { forwardRef, Fragment, ReactNode, useState } from "react"
import { Button, buttonStyles } from "./Button"
import { Icon, IconCaretDown, IconChevronUp, IconName, IconPlus, IconRef } from "./Icon"
import { dropdownItemStyles, dropdownMenuStyles } from "./menu-helpers"
import { CheckIcon } from "@radix-ui/react-icons"

type Select = VariantProps<typeof buttonStyles> & {
  icon?: IconRef
  value?: string
  className?: string
  placeholder?: string
  heading?: string
  onValueChange?: (value: string | undefined) => void
  items: (SelectItem | SelectGroup)[]
  required?: boolean
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
}

type SelectItem = {
  type?: "item"
  icon?: IconName
  label: string | ReactNode
  value: string
}

type SelectGroup = {
  type: "group"
  label: string | ReactNode
  items: SelectItem[]
}

export const Select = forwardRef<HTMLInputElement, Select>(
  ({ className, icon, loading, shape, variant, side = "bottom", ...props }, ref) => {
    const [open, setOpen] = useState(false)
    const styles = selectStyles({
      variant,
      shape: "default",
      loading,
    })
    const dropdownStyles = dropdownMenuStyles()
    const headingStyles = dropdownItemStyles({
      caption: true,
      selectable: false,
    })
    return (
      <S.Root
        open={open}
        onOpenChange={setOpen}
        value={props.value}
        onValueChange={props.onValueChange}
        required={props.required}
      >
        <S.Trigger className={cn(styles, className)}>
          <span data-part="label">
            <S.Value placeholder={props.placeholder ?? "Select..."} />
          </span>
          <S.Icon data-part="icon">
            <Icon name="chevronDown" />
          </S.Icon>
        </S.Trigger>
        <S.Portal>
          <S.Content
            position="popper"
            className={cn(
              dropdownStyles.content(),
              "min-w-[--radix-select-trigger-width] max-h-[calc(min(210px,var(--radix-select-content-available-height)))] md:max-h-[calc(min(400px,var(--radix-select-content-available-height)))]",
            )}
            side={side}
            align={props.align}
            sideOffset={3}
            avoidCollisions
          >
            {!!props.heading && (
              <div className={dropdownStyles.header()}>
                <span>{props.heading}</span>
                <Button
                  variant="ghost"
                  shape="icon"
                  icon="cross"
                  className="!size-6 rounded-sm"
                  onClick={() => setOpen(false)}
                />
              </div>
            )}
            <S.ScrollUpButton className={scrollButton({ dir: "up" })} data-disabled>
              <IconChevronUp />
            </S.ScrollUpButton>
            <S.Viewport className={cn(dropdownStyles.items())}>
              {props.items.map((item, i) => (
                <Fragment key={i}>
                  {item.type === "group" ? (
                    <S.Group className={cn(dropdownStyles.items(), "p-0")}>
                      <S.Label className={headingStyles.base()}>
                        <span className={headingStyles.indicator()}></span>
                        <span className={headingStyles.label()}>{item.label}</span>
                      </S.Label>
                      {item.items.map((item, i) => {
                        return <SelectItem key={i} {...item} />
                      })}
                    </S.Group>
                  ) : (
                    <SelectItem {...item} />
                  )}
                </Fragment>
              ))}
            </S.Viewport>
            <S.ScrollDownButton className={scrollButton({ dir: "down" })}>
              <IconCaretDown />
            </S.ScrollDownButton>
          </S.Content>
        </S.Portal>
      </S.Root>
    )
  },
)
Select.displayName = "Select"

function SelectItem(props: SelectItem) {
  const itemStyles = dropdownItemStyles()
  return (
    <S.Item
      value={props.value}
      className={itemStyles.base()}
      textValue={typeof props.label === "string" ? props.label : undefined}
    >
      <span className={itemStyles.indicator()}>
        {props.icon ? (
          <Icon name={props.icon} />
        ) : (
          <S.ItemIndicator className="el-svg:size-4">
            <CheckIcon />
          </S.ItemIndicator>
        )}
      </span>
      <span className={itemStyles.label()}>
        <S.ItemText>{props.label}</S.ItemText>
      </span>
    </S.Item>
  )
}

const selectStyles = tv({
  extend: buttonStyles,
  variants: {},
})

const scrollButton = tv({
  base: "flex cursor-default items-center justify-center py-1",
  variants: {
    dir: {
      up: "",
      down: "",
    },
  },
})
